/**
 * Shared dataLayer pushes for Google Ads / GA4 / TikTok (via Web GTM).
 *
 * Guide contracts:
 * - Google: generate_lead, add_to_cart, begin_checkout, purchase (+ ecommerce / user_data)
 * - TikTok: same events + event_id for Pixel/Events API dedup
 *
 * Meta Pixel/CAPI is FROZEN — this module is additive only. Reuse Meta event_id
 * values (lead_*, atc_*, checkout_*, purchase_*) when the caller has them.
 *
 * TikTok Pixel code is NOT hardcoded here. Prefer GTM container tags.
 * Optional env for GTM variable/docs: NEXT_PUBLIC_TIKTOK_PIXEL_CODE (browser-safe id only).
 *
 * PII safety: never put email/phone in URL, localStorage, console, or event names.
 */

const DEFAULT_CURRENCY = "IDR";

const isBrowser = () => typeof window !== "undefined";

/** Ensure dataLayer array exists so pushes queue before GTM boots. */
export const ensureDataLayer = () => {
  if (!isBrowser()) {
    return null;
  }
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

/** Optional TikTok Pixel code for GTM (no dual hardcode load). */
export const getTikTokPixelCodeHint = () => {
  const code = process.env.NEXT_PUBLIC_TIKTOK_PIXEL_CODE;
  if (!code || typeof code !== "string") {
    return "";
  }
  return code.trim();
};

export const formatEmailAddress = (email) => {
  if (email == null || email === "") {
    return "";
  }
  return String(email).trim().toLowerCase();
};

/**
 * E.164-ish phone for Enhanced Conversions / TikTok user_data.
 * Indonesia-oriented normalization (0… / 8… → +62…); already-international kept.
 */
export const formatPhoneE164 = (value) => {
  if (value == null || value === "") {
    return "";
  }
  let digits = String(value).replace(/[^0-9+]/g, "");
  if (!digits) {
    return "";
  }
  if (digits.charAt(0) === "+") {
    const rest = digits.slice(1).replace(/\D/g, "");
    return rest ? `+${rest}` : "";
  }
  digits = digits.replace(/\D/g, "");
  if (!digits) {
    return "";
  }
  if (digits.indexOf("00") === 0) {
    digits = digits.replace(/^00+/, "");
  }
  if (digits.indexOf("0") === 0) {
    return `+62${digits.replace(/^0+/, "")}`;
  }
  if (digits.indexOf("8") === 0) {
    return `+62${digits}`;
  }
  if (digits.indexOf("62") === 0) {
    return `+${digits}`;
  }
  return `+${digits}`;
};

export const formatExternalId = (value) => {
  if (value == null || value === "") {
    return "";
  }
  return String(value).trim();
};

/**
 * @param {{ email?: string, phone?: string, external_id?: string|number }} input
 * @returns {{ email_address?: string, phone_number?: string, external_id?: string }|undefined}
 */
export const buildUserData = ({ email, phone, external_id } = {}) => {
  const userData = {};
  const emailAddress = formatEmailAddress(email);
  const phoneNumber = formatPhoneE164(phone);
  const externalId = formatExternalId(external_id);
  if (emailAddress) {
    userData.email_address = emailAddress;
  }
  if (phoneNumber) {
    userData.phone_number = phoneNumber;
  }
  if (externalId) {
    userData.external_id = externalId;
  }
  return Object.keys(userData).length ? userData : undefined;
};

export const toMoneyNumber = (value) => {
  if (value == null || value === "") {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * GA4 ecommerce items shape.
 * @param {{ item_id?: string, item_name?: string, price?: number|string, quantity?: number }[]} items
 */
export const buildEcommerceItems = (items = []) => {
  if (!Array.isArray(items) || !items.length) {
    return [];
  }
  return items.map((item) => {
    const row = {
      item_id: item.item_id != null ? String(item.item_id) : "",
      item_name: item.item_name != null ? String(item.item_name) : "",
      quantity: item.quantity != null ? Number(item.quantity) || 1 : 1,
    };
    const price = toMoneyNumber(item.price);
    if (price !== undefined) {
      row.price = price;
    }
    return row;
  }).filter((row) => row.item_id || row.item_name);
};

/**
 * Build one ecommerce package item from common checkout fields.
 */
export const packageToEcommerceItem = ({
  item_id,
  item_name,
  price,
  quantity = 1,
} = {}) => buildEcommerceItems([{
  item_id: item_id || "",
  item_name: item_name || item_id || "",
  price,
  quantity,
}]);

/**
 * Low-level push. Never logs payload (may contain PII).
 * @returns {boolean} whether a push was attempted
 */
export const pushDataLayer = (payload) => {
  const dl = ensureDataLayer();
  if (!dl || !payload || typeof payload !== "object") {
    return false;
  }
  const body = { ...payload };
  // Optional TikTok readiness field for GTM variables (id only, not a second pixel install).
  const ttPixel = getTikTokPixelCodeHint();
  if (ttPixel && body.tiktok_pixel_code == null) {
    body.tiktok_pixel_code = ttPixel;
  }
  dl.push(body);
  return true;
};

/**
 * Virtual SPA page_view for GA4 / GTM History Change alternatives.
 * Does not touch Meta PageView.
 */
export const pushPageView = ({ page_path, page_location, page_title } = {}) => {
  const payload = { event: "page_view" };
  if (page_path) {
    payload.page_path = String(page_path);
  }
  if (page_location) {
    payload.page_location = String(page_location);
  }
  if (page_title) {
    payload.page_title = String(page_title);
  } else if (isBrowser() && document && document.title) {
    payload.page_title = document.title;
  }
  if (isBrowser() && !payload.page_location && window.location) {
    payload.page_location = window.location.href;
  }
  if (isBrowser() && !payload.page_path && window.location) {
    payload.page_path = `${window.location.pathname || ""}${window.location.search || ""}`;
  }
  return pushDataLayer(payload);
};

/**
 * Lead / CompleteRegistration business moment.
 * event_id should match Meta: lead_${leadId}
 */
export const pushGenerateLead = ({
  lead_id,
  event_id,
  email,
  phone,
  external_id,
  extra = {},
} = {}) => {
  const payload = {
    event: "generate_lead",
    ...extra,
  };
  if (lead_id) {
    payload.lead_id = String(lead_id);
  }
  if (event_id) {
    payload.event_id = String(event_id);
  } else if (lead_id) {
    payload.event_id = `lead_${lead_id}`;
  }
  const userData = buildUserData({ email, phone, external_id });
  if (userData) {
    payload.user_data = userData;
  }
  return pushDataLayer(payload);
};

/**
 * AddToCart business moment.
 * event_id should match Meta: atc_${checkoutId}
 */
export const pushAddToCart = ({
  event_id,
  value,
  currency = DEFAULT_CURRENCY,
  items = [],
  email,
  phone,
  external_id,
  extra = {},
} = {}) => {
  const money = toMoneyNumber(value);
  const ecommerce = {
    currency: currency || DEFAULT_CURRENCY,
    items: buildEcommerceItems(items),
  };
  if (money !== undefined) {
    ecommerce.value = money;
  }
  const payload = {
    event: "add_to_cart",
    ecommerce,
    ...extra,
  };
  if (event_id) {
    payload.event_id = String(event_id);
  }
  const userData = buildUserData({ email, phone, external_id });
  if (userData) {
    payload.user_data = userData;
  }
  return pushDataLayer(payload);
};

/**
 * InitiateCheckout → begin_checkout.
 * event_id should match Meta: checkout_${checkoutId}
 */
export const pushBeginCheckout = ({
  event_id,
  value,
  currency = DEFAULT_CURRENCY,
  items = [],
  email,
  phone,
  external_id,
  extra = {},
} = {}) => {
  const money = toMoneyNumber(value);
  const ecommerce = {
    currency: currency || DEFAULT_CURRENCY,
    items: buildEcommerceItems(items),
  };
  if (money !== undefined) {
    ecommerce.value = money;
  }
  const payload = {
    event: "begin_checkout",
    ecommerce,
    ...extra,
  };
  if (event_id) {
    payload.event_id = String(event_id);
  }
  const userData = buildUserData({ email, phone, external_id });
  if (userData) {
    payload.user_data = userData;
  }
  return pushDataLayer(payload);
};

/**
 * Purchase success.
 * event_id should match Meta: purchase_${orderId} / meta_purchase.event_id
 * transaction_id = stable backend order / invoice id
 */
export const pushPurchase = ({
  event_id,
  transaction_id,
  value,
  currency = DEFAULT_CURRENCY,
  items = [],
  email,
  phone,
  external_id,
  extra = {},
} = {}) => {
  const money = toMoneyNumber(value);
  const ecommerce = {
    currency: currency || DEFAULT_CURRENCY,
    items: buildEcommerceItems(items),
  };
  if (transaction_id) {
    ecommerce.transaction_id = String(transaction_id);
  }
  if (money !== undefined) {
    ecommerce.value = money;
  }
  const payload = {
    event: "purchase",
    ecommerce,
    ...extra,
  };
  if (event_id) {
    payload.event_id = String(event_id);
  } else if (transaction_id) {
    payload.event_id = `purchase_${transaction_id}`;
  }
  const userData = buildUserData({ email, phone, external_id });
  if (userData) {
    payload.user_data = userData;
  }
  return pushDataLayer(payload);
};

export default {
  ensureDataLayer,
  getTikTokPixelCodeHint,
  formatEmailAddress,
  formatPhoneE164,
  formatExternalId,
  buildUserData,
  buildEcommerceItems,
  packageToEcommerceItem,
  pushDataLayer,
  pushPageView,
  pushGenerateLead,
  pushAddToCart,
  pushBeginCheckout,
  pushPurchase,
};
